import React from 'react';
import { graphql } from 'gatsby';

import { useSEO } from './hooks';

export interface ISEO {
  data: Queries.SEODataFragment['data'];
  twitterCard?: string;
  twitterAuthor?: string;
  twitterSite?: string;
  noindex?: boolean;
}

const SEO: React.FC<ISEO> = ({
  data,
  twitterCard = 'summary_large_image',
  twitterAuthor = '@your_twitter_name',
  twitterSite = '@your_twitter_name',
  noindex = false,
}) => {
  const seo = useSEO(data);

  if (!seo) return null;

  const {
    url,
    title,
    ogTitle,
    twitterTitle,
    description,
    ogDescription,
    twitterDescription,
    image,
    ogImage,
    twitterImage,
  } = seo;

  return (
    <>
      <title>{title}</title>
      {url && <link rel="canonical" href={url} />}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={noindex ? 'noindex' : 'index, follow'} />
      <meta name="image" content={image.url} />
      <meta name="image:alt" content={image.alt} />

      {url && <meta property="og:url" content={url} />}
      <meta property="og:site_name" content="Shakespeare Music" />
      <meta property="og:type" content="website" />
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      <meta property="og:image" content={ogImage.url} />
      <meta property="og:image:alt" content={ogImage.alt} />

      <meta property="fb:app_id" content={process.env.GATSBY_FB_APP_ID} />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:creator" content={twitterAuthor} />
      {twitterTitle && <meta name="twitter:title" content={twitterTitle} />}
      {twitterDescription && <meta name="twitter:description" content={twitterDescription} />}
      <meta name="twitter:image" content={twitterImage.url} />
      <meta name="twitter:image:alt" content={twitterImage.alt} />
    </>
  );
};

export const query = graphql`
  fragment SEOData on SEOEnabledPage {
    ... on PrismicHome {
      data {
        seo {
          ... on PrismicHomeDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicBlogArchive {
      data {
        seo {
          ... on PrismicBlogArchiveDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicBlogArticle {
      data {
        seo {
          ... on PrismicBlogArticleDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicAuthor {
      data {
        seo {
          ... on PrismicAuthorDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicSolution {
      data {
        seo {
          ... on PrismicSolutionDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicIndustry {
      data {
        seo {
          ... on PrismicIndustryDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicDiscover {
      data {
        seo {
          ... on PrismicDiscoverDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicEuropeanUnionProjects {
      data {
        seo {
          ... on PrismicEuropeanUnionProjectsDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }

    ... on PrismicPrivacyPolicy {
      data {
        seo {
          ... on PrismicPrivacyPolicyDataSeoSeo {
            primary {
              og_description
              og_title
              page_description
              page_title
              twitter_description
              twitter_title
              twitter_image {
                url
                alt
              }
              page_image {
                url
                alt
              }
              og_image {
                url
                alt
              }
            }
          }
        }
      }
    }
  }
`;

export default SEO;
